import { tv } from "@ui/lib/tv";
import { Radio, RadioGroup } from "@ui/primitives/RadioGroup";
import type { ReactNode } from "react";

const optionGrid = tv({
  slots: {
    root: "grid grid-cols-2 gap-3 lg:grid-cols-3",
    item: [
      "flex cursor-pointer flex-col gap-0.5 rounded-xl border-2 px-4 py-3 text-left outline-none",
      "hover:border-neutral-300",
    ],
    primary: "font-semibold text-neutral-900 text-sm",
    secondary: "text-neutral-500 text-sm",
  },
  variants: {
    selected: {
      true: { item: "border-green-600 bg-green-50" },
      false: { item: "border-neutral-200 bg-white" },
    },
    focusVisible: {
      true: { item: "ring-2 ring-green-500" },
    },
  },
});

export interface OptionGridItem {
  id: string;
  /** First line: the option's name. */
  primary: ReactNode;
  /** Optional second line: supporting detail (e.g. region, code). */
  secondary?: ReactNode;
}

export interface OptionGridProps {
  items: OptionGridItem[];
  selectedId: string;
  onSelect: (id: string) => void;
  /** Accessible name for the radio group (e.g. the section heading's text). */
  ariaLabel: string;
  className?: string;
}

/**
 * Selectable option-card grid: two columns (three at `lg`) of bordered cards
 * with a primary and optional secondary line; the selected card is marked with
 * a green border/fill. Built on the RadioGroup primitive, so the grid exposes
 * radiogroup semantics with a single tab stop and arrow-key roving focus. Used
 * for settings-style pickers such as language and currency.
 */
export function OptionGrid({ items, selectedId, onSelect, ariaLabel, className }: OptionGridProps) {
  const styles = optionGrid();

  return (
    <RadioGroup aria-label={ariaLabel} className={styles.root({ className })} onChange={onSelect} value={selectedId}>
      {items.map((item) => (
        <Radio
          className={({ isSelected, isFocusVisible }) =>
            styles.item({ selected: isSelected, focusVisible: isFocusVisible })
          }
          key={item.id}
          value={item.id}
        >
          <span className={styles.primary()}>{item.primary}</span>
          {item.secondary != null && <span className={styles.secondary()}>{item.secondary}</span>}
        </Radio>
      ))}
    </RadioGroup>
  );
}
